import { useState } from 'react';
import type React from 'react';
import { useAuth } from '../../../contexts/AuthContext';

const BLUE = '#0072EA';

const VALUES = [
  {
    id: 'aim-high',
    number: '01',
    title: 'Aim High',
    tagline: 'We are bold and ambitious to create impact',
    bullets: [
      'Push boundaries and think big',
      'Challenge processes, standards and outcomes',
      'Hold ourselves and others accountable to the highest standards',
      'Are courageous and decisive',
      'Prioritize and focus on creating impact',
    ],
  },
  {
    id: 'own-it',
    number: '02',
    title: 'Own It',
    tagline: 'We take initiative and get things done',
    bullets: [
      'Are proactive and work efficiently',
      'Embrace challenges and mistakes as opportunities for growth',
      'Make decisions that move our company forward',
      'Take responsibility for our careers',
      'Act quickly and change course with full commitment',
    ],
  },
  {
    id: 'win-together',
    number: '03',
    title: 'Win Together',
    tagline: 'We support each other to succeed together',
    bullets: [
      'Collaborate globally and across departments',
      "Overcome obstacles as a team and celebrate each other's successes",
      'Have fun together',
      'Guide and develop others',
      'Have confidence and trust in our colleagues',
    ],
  },
  {
    id: 'embrace-openness',
    number: '04',
    title: 'Embrace Openness',
    tagline: 'We are inclusive, honest and foster diversity',
    bullets: [
      'Ask for and provide honest feedback',
      'Treat each other equally and respectfully',
      'Listen to others and respect their views',
      'Challenge our biases',
      'Are honest and transparent',
      'Are compassionate and stand up for others',
    ],
  },
  {
    id: 'obsess-customers',
    number: '05',
    title: 'Obsess About Customers',
    tagline: 'We delight customers and champion their needs',
    bullets: [
      "Take time to understand our customers' needs",
      'Make decisions with our subscribers in mind',
      'Deliver true value to our customers',
      'Build lasting and trusting relationships with our customers',
    ],
  },
];

function getGreeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(email: string | null | undefined): string {
  if (!email) return 'there';
  const local = email.split('@')[0];
  return local.split('.')[0].replace(/^./, (c) => c.toUpperCase());
}

const DOCK_SCALES = [1.28, 1.1, 1.04];

function getDockStyle(idx: number, hoveredIdx: number | null): React.CSSProperties {
  const base = 'transform 220ms cubic-bezier(0.34, 1.4, 0.64, 1)';
  // transform-origin is set to 'center bottom' on the element so all scale
  // growth goes downward into the generous paddingBottom — never upward into
  // the overflow-y:hidden clipping boundary of the scroll container.
  if (hoveredIdx === null) return { transform: 'scale(1)', transition: base, zIndex: 1 };
  const d = Math.abs(idx - hoveredIdx);
  const scale = DOCK_SCALES[d] ?? 1;
  return {
    transform: `scale(${scale})`,
    transition: base,
    zIndex: d === 0 ? 30 : d === 1 ? 20 : 1,
  };
}

export default function HomeView() {
  const { user } = useAuth();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const hour = new Date().getHours();
  const greeting = getGreeting(hour);
  const firstName = getFirstName(user?.email);

  return (
    <div
      className="relative -m-6 min-h-screen overflow-hidden"
      style={{ background: '#080808' }}
    >
      {/* Dot grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      {/* Blue ambient glow — hero */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: '480px',
          background: `radial-gradient(ellipse 70% 50% at 50% -10%, ${BLUE}22 0%, transparent 100%)`,
        }}
      />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div className="relative px-10 pt-7 pb-7">
        <div className="flex items-center justify-between">
          <div>
            {/* Greeting */}
            <h1 className="text-[38px] font-black text-white leading-[1.1] tracking-tight">
              {greeting}, <span style={{ color: BLUE }}>{firstName}</span> 👋
            </h1>
            {/* Subtitle */}
            <p className="text-[14px] text-zinc-500 mt-1.5">
              Your hub for everything People at FINN. Grow, support, and celebrate FINNies.
            </p>
          </div>

          {/* FINN wordmark */}
          <span className="text-white font-black text-xl tracking-[0.04em] shrink-0">FINN</span>
        </div>

        {/* Divider */}
        <div
          className="mt-6 h-px"
          style={{
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.08) 70%, transparent)',
          }}
        />
      </div>

      {/* ── VALUES ───────────────────────────────────────────── */}
      <div className="relative pb-14">
        {/* Section header — stays padded */}
        <div className="flex items-center gap-4 mb-8 px-10">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
            Core Values
          </span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.05)' }} />
          <span className="text-[10px] font-mono text-zinc-700">05</span>
        </div>

        {/* Scroll hint */}
        <div className="flex items-center justify-center gap-3 mb-6 px-10">
          <div className="flex items-center gap-1.5 text-zinc-700">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="flex gap-1.5">
            {VALUES.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: hoveredIdx === i ? '16px' : '4px',
                  height: '4px',
                  background: hoveredIdx === i ? BLUE : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-zinc-700">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Horizontal snap-scroll track — breaks out of px-10 */}
        {/* paddingBottom is generous so scaled cards (transform-origin: bottom) */}
        {/* have room to grow downward without hitting overflow-y:hidden clip.  */}
        <style>{`.values-track::-webkit-scrollbar{display:none}`}</style>
        <div
          className="values-track flex gap-4 overflow-x-auto"
          style={{
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            paddingLeft: 'calc(50% - 180px)',
            paddingRight: 'calc(50% - 180px)',
            paddingBottom: '120px',
            paddingTop: '10px',
          }}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          {VALUES.map((v, i) => (
            <div
              key={v.id}
              className="group relative flex flex-col rounded-2xl overflow-hidden will-change-transform flex-shrink-0"
              style={{
                width: '360px',
                scrollSnapAlign: 'center',
                transformOrigin: 'center top',
                background: '#111111',
                border: hoveredIdx === i
                  ? '1px solid rgba(0,114,234,0.4)'
                  : '1px solid rgba(255,255,255,0.06)',
                boxShadow: hoveredIdx === i
                  ? '0 20px 70px -10px rgba(0,114,234,0.4)'
                  : '0 0 0 0 transparent',
                ...getDockStyle(i, hoveredIdx),
              }}
              onMouseEnter={() => setHoveredIdx(i)}
            >
              {/* Ghost watermark number */}
              <span
                className="absolute bottom-4 right-5 text-[96px] font-black leading-none select-none pointer-events-none"
                style={{ color: 'rgba(255,255,255,0.022)' }}
              >
                {v.number}
              </span>

              <div className="relative flex flex-col gap-5 p-7 flex-1">
                {/* Number badge */}
                <span
                  className="text-[11px] font-bold font-mono"
                  style={{ color: `${BLUE}80` }}
                >
                  {v.number}
                </span>

                {/* Title */}
                <h3 className="text-[22px] font-black text-white leading-tight tracking-tight">
                  {v.title}
                </h3>

                {/* Thin divider */}
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)' }} />

                {/* Tagline */}
                <p
                  className="text-[13px] font-semibold leading-snug"
                  style={{ color: BLUE }}
                >
                  {v.tagline}
                </p>

                {/* Bullets */}
                <ul className="space-y-2.5 flex-1">
                  {v.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        className="mt-[7px] shrink-0 w-[3px] h-[3px] rounded-full"
                        style={{ background: 'rgba(255,255,255,0.25)' }}
                      />
                      <span className="text-[13px] text-zinc-400 leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Bottom glow bar */}
              <div
                className="h-[2px] w-full transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(to right, transparent, ${BLUE}, transparent)`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
